var _Object$getOwnPropert;
import { WINDOW_BROKER_LOCATOR_LOGGER_NAME, WINDOW_BROKER_PROPERTY_NAME } from '../config';
import { Logger } from 'simple-logging-lib';
import { WindowBroker } from './window-broker';
import { WindowBrokerProxy, checkIfHasBrokerProxy } from './window-broker-proxy';
const isScopeSameOrigin = scope => {
  try {
    return scope.location.origin === window.location.origin;
  } catch (e) {
    return false;
  }
};
export const isUsingMagpie = async scope => {
  if (isScopeSameOrigin(scope)) {
    const hasWindowBroker = Boolean(scope[WINDOW_BROKER_PROPERTY_NAME]);
    return Promise.resolve(hasWindowBroker);
  }
  return Boolean(await checkIfHasBrokerProxy(scope));
};
const ownParentGetter = (_Object$getOwnPropert = Object.getOwnPropertyDescriptor(window, 'parent')) === null || _Object$getOwnPropert === void 0 ? void 0 : _Object$getOwnPropert.get;
const getParentScope = w => {
  return ownParentGetter ? ownParentGetter.call(w) : w.parent;
};
const getScopesChain = () => {
  const scopeChain = new Set();
  for (let currentWindow = window; currentWindow && !scopeChain.has(currentWindow); currentWindow = getParentScope(currentWindow)) {
    scopeChain.add(currentWindow);
  }
  // Reverse the order to start from top-most scope
  return Array.from(scopeChain).reverse();
};
export const findTopMostScopeBy = async (predicate, defaultTo) => {
  const scopeChain = getScopesChain();
  const results = await Promise.all(scopeChain.map(predicate));
  // Return top-most scope that satisfies the predicate (scopeChain is sorted)
  const index = results.findIndex(Boolean);
  return index !== -1 ? scopeChain[index] : defaultTo;
};
export const findTopMostScopeWithMagpie = () => findTopMostScopeBy(isUsingMagpie, null);
const findTopMostScopeOfSameOrigin = () => {
  if (window === window.top) {
    return Promise.resolve(window);
  }
  if (!window.top) {
    return Promise.resolve(window);
  }
  return findTopMostScopeBy(isScopeSameOrigin, window);
};

/*
If there isn't a window in the tab that uses Magpie, create a new instance of
`WindowBroker` and assign it to the top-most same-origin window.
If there is a window in the tab that uses Magpie, check if it's same-origin and
accessible. If it is, return the instance. If it's not, create a proxy instance
and return it.
*/
export const getOrCreateBroker = async () => {
  const logger = Logger.getLogger(WINDOW_BROKER_LOCATOR_LOGGER_NAME);
  const topMostScopeWithMagpie = await findTopMostScopeWithMagpie();

  // If no scope uses Magpie
  if (!topMostScopeWithMagpie) {
    const broker = WindowBroker.getInstance();
    // Find scope closest to the top that is same-origin and thus accessible
    const topMostScopeOfSameOrigin = await findTopMostScopeOfSameOrigin();
    logger.debug('No existing WindowBroker instance found. Instantiating new at scope:', topMostScopeOfSameOrigin);
    Object.assign(topMostScopeOfSameOrigin, {
      [WINDOW_BROKER_PROPERTY_NAME]: broker
    });
    return broker;
  }

  // A scope uses magpie - check if it's of same-origin and directly accessible
  if (isScopeSameOrigin(topMostScopeWithMagpie)) {
    logger.debug('WindowBroker instance is available at a scope', topMostScopeWithMagpie);
    return topMostScopeWithMagpie[WINDOW_BROKER_PROPERTY_NAME];
  } else {
    logger.debug('WindowBroker instance is available at a scope through proxy', topMostScopeWithMagpie);
    return WindowBrokerProxy.create(topMostScopeWithMagpie);
  }
};

/**
 * Deletes the WindowBroker instance at the top-most scope that uses Magpie.
 * Atm the main use case for this function is to reset and delete the broker
 * for testing purposes.
 * @returns A promise that resolves when the broker is deleted.
 */
export const deleteBroker = async () => {
  const logger = Logger.getLogger(WINDOW_BROKER_LOCATOR_LOGGER_NAME);
  const topMostScopeWithMagpie = await findTopMostScopeWithMagpie();
  if (!topMostScopeWithMagpie) {
    logger.debug('No WindowBroker instance found to delete');
    return;
  }
  logger.debug('Deleting WindowBroker instance at scope', topMostScopeWithMagpie);
  const broker = topMostScopeWithMagpie[WINDOW_BROKER_PROPERTY_NAME];
  await broker.reset();
  delete topMostScopeWithMagpie[WINDOW_BROKER_PROPERTY_NAME];
};